import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, LogOut, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { roleLabel } from "../../lib/roleLabels";

/**
 * Auth Faza 5 — Global impersonation banner.
 *
 * Visible on every admin page whenever `/auth/me` reports
 * `is_impersonation === true`. Sticky to the top of the layout, orange so it
 * cannot be confused with a normal nav element, and exposes a single
 * "Oprește impersonarea" action that hits the backend stop endpoint, which
 * regenerates the owner cookie + hard-reloads the SPA.
 *
 * Props:
 *   me — { email, role, is_impersonation, impersonated_by_email }
 *
 * Returns null when not impersonating so the banner adds zero visual weight
 * outside the impersonation path.
 */
export default function ImpersonationBanner({ me }) {
  const [stopping, setStopping] = useState(false);

  if (!me?.is_impersonation) return null;

  const stop = async () => {
    setStopping(true);
    try {
      await api.post("/admin/auth/impersonation/stop");
      toast.success("Impersonare oprită. Revii la sesiunea owner.");
      // Hard reload so /auth/me re-fetches under the owner cookie and the
      // whole sidebar/permissions tree rebuilds. SPA navigation would
      // race with the cookie rotation we just received.
      setTimeout(() => { window.location.href = "/admin/users"; }, 600);
    } catch (err) {
      // Hotfix M921 — never leave the operator trapped in impersonation.
      // If the stop endpoint genuinely fails (owner deleted, JWT tampered,
      // server error), fall back to a hard logout + bounce to login with a
      // clear Romanian message instead of just showing a toast and keeping
      // the impersonation banner up forever.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message || "";
      toast.error(detail || "Nu am putut opri impersonarea.");
      if (status && status !== 401) {
        try { await api.post("/auth/logout"); } catch (_e) { /* best effort */ }
        toast.warning(
          "Sesiunea de impersonare a expirat. Te rugăm să te autentifici din nou.",
        );
        setTimeout(() => { window.location.href = "/admin-login"; }, 1200);
        return;
      }
      setStopping(false);
    }
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 w-full bg-[#D97706] text-white shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-start sm:items-center gap-2 text-sm">
          <AlertTriangle size={16} className="shrink-0 mt-0.5 sm:mt-0" />
          <div className="leading-snug">
            <strong className="font-semibold">Impersonare activă:</strong>{" "}
            acționezi ca{" "}
            <span className="font-semibold" data-testid="impersonation-target">
              {me.email}
            </span>{" "}
            (<span className="uppercase tracking-widest text-[11px]">{roleLabel(me.role)}</span>).
            <span className="ml-2 opacity-90 text-[12px]">
              Inițiată de{" "}
              <span data-testid="impersonation-actor">{me.impersonated_by_email}</span>.
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={stop}
          disabled={stopping}
          className="inline-flex items-center gap-2 text-sm font-semibold bg-white text-[#9b4806] hover:bg-[#FED7AA] rounded-full px-4 py-1.5 transition-colors disabled:opacity-60 shrink-0"
          data-testid="impersonation-stop-btn"
        >
          {stopping ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
          Oprește impersonarea
        </button>
      </div>
    </div>
  );
}
